import { io } from 'socket.io-client';
import store from '@/store';
import router from '@/router';

const getSocketBaseUrl = () => {
    const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '';
    return apiBaseUrl
        .replace(/\/api\/admin\/?$/, '')
        .replace(/\/api\/?$/, '')
        .replace(/\/admin\/?$/, '');
};

const getMessagePreview = message => {
    const text = String(message?.message || '').trim();
    if (text) {
        return text.length > 120 ? `${text.slice(0, 117)}...` : text;
    }
    const attachmentsCount = Array.isArray(message?.attachments) ? message.attachments.length : 0;
    if (attachmentsCount > 1) {
        return `${attachmentsCount} вложения`;
    }
    if (attachmentsCount === 1) {
        return 'Вложение';
    }
    return `Обращение #${message?.chat_id || ''}`;
};

class ChatNotificationService {
    constructor() {
        this.socket = null;
        this.audio = null;
        this.isInitialized = false;
        this.started = false;
        this.seenMessageIds = new Set();
        this.onMessage = this.onMessage.bind(this);
    }

    init() {
        if (this.isInitialized) {
            return;
        }

        this.audio = new Audio();
        this.audio.preload = 'auto';
        try {
            this.audio.src = require('@/assets/mp3/new_order.mp3');
        } catch (error) {
            this.audio.src = '/assets/mp3/new_order.mp3';
        }

        this.isInitialized = true;
    }

    start() {
        if (this.started) {
            return;
        }

        const token = localStorage.getItem('user-token');
        if (!token) {
            return;
        }

        this.init();
        this.started = true;

        this.socket = io(getSocketBaseUrl(), {
            path: '/socket.io',
            reconnection: true,
            transports: ['websocket'],
            auth: {
                role: 'admin',
                token
            },
            query: {
                role: 'admin',
                token
            }
        });

        this.socket.on('chat:message:new', this.onMessage);
    }

    stop() {
        this.started = false;
        if (this.socket) {
            this.socket.off('chat:message:new', this.onMessage);
            this.socket.disconnect();
            this.socket = null;
        }
    }

    onMessage(message) {
        if (!message || message.sender_type !== 'customer') {
            return;
        }

        const messageId = Number(message.id);
        if (messageId && this.seenMessageIds.has(messageId)) {
            return;
        }
        if (messageId) {
            this.seenMessageIds.add(messageId);
            if (this.seenMessageIds.size > 500) {
                const first = this.seenMessageIds.values().next().value;
                this.seenMessageIds.delete(first);
            }
        }

        if (this.isViewingChat(message.chat_id)) {
            return;
        }

        this.playNotificationSound();
        this.showBrowserNotification(message);
    }

    isViewingChat(chatId) {
        if (document.visibilityState !== 'visible') {
            return false;
        }

        const route = router.currentRoute;
        if (route.name === 'Chat' && Number(route.params.chatId) === Number(chatId)) {
            return true;
        }

        if (
            route.name === 'Customer'
            && route.query
            && Number(route.query.chat_id) === Number(chatId)
        ) {
            return true;
        }

        return false;
    }

    playNotificationSound() {
        if (!this.audio || !store.state.settings.chatSoundsOn) {
            return;
        }

        try {
            this.audio.currentTime = 0;
            this.audio.play().catch(() => {});
        } catch (e) {
            // ignore playback errors (autoplay policy)
        }
    }

    showBrowserNotification(message) {
        if (!('Notification' in window) || Notification.permission !== 'granted') {
            return;
        }

        const chatId = Number(message.chat_id);
        const notification = new Notification('Новое сообщение в чате', {
            body: getMessagePreview(message),
            icon: '/favicon.ico',
            tag: `chat-${chatId}`,
            renotify: true
        });

        notification.onclick = () => {
            try {
                window.focus();
            } catch (e) {
                // ignore
            }
            if (chatId) {
                router.push({
                    name: 'Chat',
                    params: { chatId }
                }).catch(() => {});
            }
            notification.close();
        };
    }

    requestNotificationPermission() {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }
}

const chatNotificationService = new ChatNotificationService();

export default chatNotificationService;
